import { Box, Skeleton } from '@mui/material';
import dayjs from 'dayjs';
import { useState } from 'react';
import { KitButton, KitPill } from '../common/MobileKit';
import MobileSheet from '../common/MobileSheet';
import { protoIcon } from '../common/ProtoIcon';
import {
    KitChip,
    KitChips,
    KitEmpty,
    KitEntityCard,
    KitFab,
    KitField,
    KitInput,
    KitSearch,
    KitSelect,
    KitStickyToolbar,
    KitToolbarButton,
} from '../common/MobileKitExtras';
import { useAppBarAction } from '../layout/AppBarActions';
import { quoteDateLabel, quoteStatusView } from './quoteMobileUtils';

/**
 * Lista de orçamentos no shell mobile — `prototipos-mobile/orcamentos/lista.html`.
 * Só apresentação: filtros, paginação, permissões e navegação vêm de `pages/service_quotes/List.js`.
 */

const CalendarIcon = protoIcon('calendar');
const SlidersIcon = protoIcon('sliders');
const PlusIcon = protoIcon('plus');
const QuotesIcon = protoIcon('quote');

// Altura da barra superior "grande" das telas raiz (10 + 40 + título 28px/1.4 + 2 + 6 + 6) + safe-area.
const LARGE_BAR_OFFSET = 'calc(103px + env(safe-area-inset-top, 0px))';

// Chips de status: valores reais da API (o protótipo só ilustra Em aberto/Aprovados/…).
const STATUS_CHIPS = [
    { value: '', label: 'Todos' },
    { value: 'novo', label: 'Novos' },
    { value: 'enviado para cliente', label: 'Enviados' },
    { value: 'aguardando aprovacao', label: 'Aguardando' },
    { value: 'aprovado', label: 'Aprovados' },
    { value: 'ordem gerada', label: 'Ordens Geradas' },
];

const toInput = (value) => (value && value.isValid() ? value.format('YYYY-MM-DD') : '');
const fromInput = (text) => {
    if (!text) return null;
    const parsed = dayjs(text, 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed : null;
};

function QuoteCard({ quote, onOpen }) {
    const view = quoteStatusView(quote.status);
    return (
        <KitEntityCard stripeColor={view.color} onClick={() => onOpen(quote)}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <Box component="span" sx={{ fontWeight: 800, fontSize: 15, letterSpacing: '-.01em' }}>Orçamento #{quote.number}</Box>
                <KitPill tone={view.tone}>{view.label}</KitPill>
            </Box>
            <Box sx={{ m: '8px 0 10px' }}>
                <Box sx={{ fontWeight: 600, fontSize: 14, lineHeight: 1.4, color: 'text.primary' }}>{quote.customer_name || '—'}</Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', fontSize: 12, color: 'text.disabled', lineHeight: 1.4 }}>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <CalendarIcon size={13} />{quoteDateLabel(quote)}
                </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px dashed', borderColor: 'divider', mt: '12px', pt: '10px' }}>
                <Box component="span" sx={{ fontWeight: 800, fontSize: 16, letterSpacing: '-.02em' }}>R$ {quote.amount ?? '0,00'}</Box>
                <Box component="span" sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.4 }} />
            </Box>
        </KitEntityCard>
    );
}

export default function MobileQuotesList({
    quotes,
    loading,
    totalCount,
    hasMore,
    onLoadMore,
    status,
    onStatusChange,
    search,
    onSearchChange,
    customers,
    customer,
    appliedPeriod,
    onApplyFilters,
    canCreate,
    onNew,
    onOpen,
}) {
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [draftCustomer, setDraftCustomer] = useState(customer);
    const [draftStart, setDraftStart] = useState(appliedPeriod.start);
    const [draftEnd, setDraftEnd] = useState(appliedPeriod.end);

    const openFilters = () => {
        setDraftCustomer(customer);
        setDraftStart(appliedPeriod.start);
        setDraftEnd(appliedPeriod.end);
        setFiltersOpen(true);
    };

    useAppBarAction({ label: 'Filtros', icon: SlidersIcon, onClick: openFilters });

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            <KitStickyToolbar top={LARGE_BAR_OFFSET}>
                <KitSearch
                    value={search}
                    onChange={(e) => onSearchChange(e.target.value)}
                    placeholder="Número ou cliente…"
                    inputProps={{ 'aria-label': 'Buscar orçamento', inputMode: 'search', enterKeyHint: 'search' }}
                />
                <KitToolbarButton aria-label="Filtros" onClick={openFilters}>
                    <SlidersIcon size={20} />
                </KitToolbarButton>
            </KitStickyToolbar>

            <KitChips>
                {STATUS_CHIPS.map((chip) => (
                    <KitChip
                        key={chip.value || 'all'}
                        active={status === chip.value}
                        count={status === chip.value && !loading ? totalCount : undefined}
                        onClick={() => onStatusChange(chip.value)}
                    >
                        {chip.label}
                    </KitChip>
                ))}
            </KitChips>

            <Box sx={{ mt: '12px' }} />

            {loading && quotes.length === 0 && (
                <Box sx={{ mt: '18px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {[1, 2, 3].map((n) => <Skeleton key={n} variant="rounded" height={132} sx={{ borderRadius: '16px' }} />)}
                </Box>
            )}

            {!loading && quotes.length === 0 && (
                <KitEmpty icon={QuotesIcon} title="Nenhum orçamento encontrado">
                    Ajuste a busca ou os filtros para ver outros orçamentos.
                </KitEmpty>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {quotes.map((quote) => (
                    <QuoteCard key={quote.service_quote_id} quote={quote} onOpen={onOpen} />
                ))}
            </Box>

            {hasMore && (
                <KitButton block sx={{ mt: '14px' }} onClick={onLoadMore} disabled={loading}>
                    {loading ? 'Carregando…' : 'Carregar mais'}
                </KitButton>
            )}

            {canCreate && <KitFab aboveTabbar label="Novo" icon={PlusIcon} onClick={onNew} />}

            <MobileSheet
                open={filtersOpen}
                onClose={() => setFiltersOpen(false)}
                title="Filtros"
                footer={(
                    <KitButton
                        variant="primary"
                        sx={{ flex: 1 }}
                        onClick={() => {
                            setFiltersOpen(false);
                            onApplyFilters({ customer: draftCustomer, start: draftStart, end: draftEnd });
                        }}
                    >
                        Aplicar
                    </KitButton>
                )}
            >
                <KitField label="Cliente">
                    <KitSelect value={draftCustomer} onChange={(e) => setDraftCustomer(e.target.value)} aria-label="Cliente">
                        <option value="">Todos</option>
                        {customers.map((c) => (
                            <option key={c.ID} value={c.ID}>{c.Name}</option>
                        ))}
                    </KitSelect>
                </KitField>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                    <KitField label="De">
                        <KitInput type="date" aria-label="Data inicial" value={toInput(draftStart)} onChange={(e) => setDraftStart(fromInput(e.target.value))} />
                    </KitField>
                    <KitField label="Até">
                        <KitInput type="date" aria-label="Data final" value={toInput(draftEnd)} onChange={(e) => setDraftEnd(fromInput(e.target.value))} />
                    </KitField>
                </Box>
            </MobileSheet>
        </Box>
    );
}
